<script lang="ts" setup>
import { useTranslate } from '@tolgee/vue'
import { computed } from 'vue'

import { usePublicData } from '@/stores/PublicData'
import { useEditorStore } from '@/stores/Editor'
import { constants } from '@/constants'
import { EVENT_TYPES, useEditorFrame } from '@/composables/iframe'

import EditorSettingsHero from '@/components/editor/EditorSettingsHero.vue'
import EditorSettingsImpact from '@/components/editor/EditorSettingsImpact.vue'
import EditorSettingsGoalsAchievements from '@/components/editor/EditorSettingsGoalsAchievements.vue'
import EditorSettingsBackground from '@/components/editor/EditorSettingsBackground.vue'
import type { LanguageType } from '@/api/interfaces'

const publicStore = usePublicData()
const editorStore = useEditorStore()
const { dispatchMessage, getIframeEl } = useEditorFrame()
const { t } = useTranslate('local')

const handleLanguageChange = () => {
  dispatchMessage(getIframeEl(), {
    type: EVENT_TYPES.UPDATE_PUBLIC_STORE,
    payload: { lang: publicStore.lang },
  })
}

const selectedLanguages = computed(() => {
  const defaultLanguage: Record<'en', string> = {
    en: constants.languages.en,
  }
  const sorted = Object.entries(constants.languages)
    .sort((a, b) => a[1].localeCompare(b[1]))
    .reduce(
      (acc, [key, value]) => {
        acc[key as LanguageType] = value
        return acc
      },
      {} as Record<LanguageType, string>,
    )
  return {
    ...defaultLanguage,
    ...sorted,
  } satisfies Record<LanguageType, string>
})
</script>

<template>
  <aside class="editor-sidebar">
    <div class="editor-sidebar-inner">
      <h2 class="editor-sidebar-title">{{ t('EditorSidebar.welcome.title') }}</h2>
      <p class="editor-sidebar-description">{{ t('EditorSidebar.welcome.subtitle') }}</p>
      <v-form v-model="editorStore.valid">
        <v-tabs v-model="publicStore.lang" @update:model-value="handleLanguageChange">
          <v-tab v-for="(lang, key) in selectedLanguages" :key="key" :value="key">
            {{ lang }}
            <template v-if="key === 'en'">({{ t('EditorSidebar.default') }})</template>
          </v-tab>
        </v-tabs>
        <v-window v-model="publicStore.lang">
          <v-window-item v-for="(lang, key) in selectedLanguages" :key="key" :value="key">
            <EditorSettingsBackground />
            <EditorSettingsHero />
            <EditorSettingsImpact />
            <EditorSettingsGoalsAchievements />
          </v-window-item>
        </v-window>
      </v-form>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.editor-sidebar {
  overflow-y: auto;
  background-color: var(--lightest);
  grid-area: sidebar;
  padding: 16px 16px 32px;

  @include media('md-and-up') {
    padding: 32px 16px;
  }
}

.editor-sidebar-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;

  @include media('md-and-up') {
    font-size: 24px;
  }
}

.editor-sidebar-description {
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 32px;

  @include media('md-and-up') {
    font-size: 20px;
  }
}

.v-tab {
  text-transform: capitalize;
  min-height: 32px;
  font-size: 20px;
  letter-spacing: 0;

  &--selected {
    color: var(--ui-green);
    font-weight: 700;
  }
}
</style>
